import React from 'react';
import toast from 'react-hot-toast';
import {
  decrementOrderQuantity,
  formatBundleOrderSummary,
  getBundleExplainer,
  getBundleStockHint,
  getQuickQuantityPresets,
  incrementOrderQuantity,
  normalizeOrderQuantity,
} from '../../utils/orderQuantity';

function MinusIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" d="M5 12h14" />
    </svg>
  );
}

function PlusIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" d="M12 5v14M5 12h14" />
    </svg>
  );
}

function BundleExplainerCard({ moq, compact }) {
  const info = getBundleExplainer(moq);

  if (moq === 1) {
    return (
      <div className={`rounded-xl border border-gray-200 bg-white ${compact ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
        <p className={`font-semibold text-gray-900 ${compact ? 'text-xs' : 'text-sm'}`}>{info.title}</p>
        <p className={`text-gray-600 mt-1 ${compact ? 'text-[10px] leading-snug' : 'text-xs'}`}>{info.body}</p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-brand-200 bg-brand-50/90 ${
        compact ? 'px-3 py-2.5' : 'px-4 py-3.5'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {/* Visual: 1 bundle = N pieces */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={`inline-flex flex-col items-center justify-center rounded-lg bg-white border border-brand-200 font-bold text-brand-800 ${
              compact ? 'px-2 py-1 min-w-[52px] text-[10px]' : 'px-2.5 py-1.5 min-w-[60px] text-xs'
            }`}
          >
            <span className="uppercase tracking-wide text-[9px] text-brand-600 font-semibold">1 bundle</span>
            <span className={compact ? 'text-sm' : 'text-base'}>{info.piecesPerBundle}</span>
            <span className="text-[9px] text-gray-500 font-medium">pieces</span>
          </span>
          <span className="text-brand-600 font-bold text-sm" aria-hidden>
            =
          </span>
          <span
            className={`text-gray-600 ${compact ? 'text-[10px] max-w-[72px]' : 'text-xs max-w-[100px]'} leading-snug`}
          >
            minimum order size
          </span>
        </div>
        <div className="hidden sm:block w-px h-10 bg-brand-200 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className={`font-semibold text-brand-900 ${compact ? 'text-xs' : 'text-sm'}`}>
            {info.title}
          </p>
          <p className={`text-brand-800/90 mt-0.5 ${compact ? 'text-[10px] leading-snug' : 'text-xs leading-relaxed'}`}>
            {info.body}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function OrderQuantitySelector({
  moq,
  total,
  onChange,
  maxQty,
  availableStock,
  onAddToCart,
  addToCartLabel = 'Add to cart',
  outOfStockLabel = 'Out of stock',
  showAddToCart = true,
  variant = 'page',
}) {
  const isCompact = variant === 'modal';
  const explainer = getBundleExplainer(moq);
  const summary = formatBundleOrderSummary(total, moq);
  const stockHint = getBundleStockHint(moq, { max: maxQty });
  const presets = getQuickQuantityPresets(moq, {
    max: maxQty,
    maxOptions: isCompact ? 5 : 6,
  });
  const canDecrease = total > moq;
  const canIncrease =
    incrementOrderQuantity(total, moq, maxQty) !== total;
  const isOutOfStock =
    availableStock == null || availableStock < moq;

  const handleInputChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(moq);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      onChange(moq);
      return;
    }
    const normalized = normalizeOrderQuantity(parsed, {
      step: moq,
      min: moq,
      max: maxQty,
    });
    if (normalized !== parsed) {
      const msg =
        moq === 1
          ? 'Enter a valid quantity'
          : `Order in full bundles only (${moq} pieces per bundle)`;
      toast(msg, { icon: 'ℹ️' });
    }
    onChange(normalized);
  };

  const stepper = (
    <div className="min-w-0 flex-1">
      <label
        className={`block font-medium text-gray-700 mb-1 ${isCompact ? 'text-[10px]' : 'text-xs'}`}
      >
        {moq === 1 ? 'Pieces' : 'Total pieces'}
      </label>
      <div
        className={`flex items-center rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm ${
          isCompact ? 'h-11' : 'h-12'
        }`}
      >
        <button
          type="button"
          aria-label={`Remove one bundle (${explainer.stepLabel})`}
          onClick={() => onChange(decrementOrderQuantity(total, moq, moq))}
          disabled={!canDecrease}
          className="flex items-center justify-center w-10 sm:w-11 h-full text-gray-600 hover:bg-gray-50 disabled:opacity-30 shrink-0"
        >
          <MinusIcon />
        </button>
        <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-1">
          <input
            type="number"
            min={moq}
            step={moq}
            max={availableStock}
            value={total}
            onChange={handleInputChange}
            aria-label={moq === 1 ? 'Number of pieces' : 'Total pieces across all bundles'}
            className={`w-full font-bold text-center bg-transparent focus:outline-none focus:ring-0 ${
              isCompact ? 'text-base' : 'text-lg'
            } ${maxQty != null && total >= maxQty ? 'text-amber-700' : 'text-gray-900'}`}
          />
          {moq > 1 && (
            <span className="text-[9px] text-brand-700 font-medium -mt-0.5">
              = {summary.headline}
            </span>
          )}
        </div>
        <button
          type="button"
          aria-label={`Add one bundle (${explainer.stepLabel})`}
          onClick={() => onChange(incrementOrderQuantity(total, moq, maxQty))}
          disabled={!canIncrease}
          className="flex items-center justify-center w-10 sm:w-11 h-full text-gray-600 hover:bg-gray-50 disabled:opacity-30 shrink-0"
        >
          <PlusIcon />
        </button>
      </div>
      {moq > 1 && (
        <p className={`text-gray-500 mt-1 ${isCompact ? 'text-[9px]' : 'text-[10px]'}`}>
          +/− adds {explainer.stepLabel}
        </p>
      )}
    </div>
  );

  const addButton = showAddToCart && (
    <button
      type="button"
      disabled={isOutOfStock || total < moq}
      onClick={onAddToCart}
      className={`font-semibold text-white rounded-xl transition-all shrink-0 w-full sm:w-auto
        ${isCompact ? 'h-11 px-5 text-sm' : 'h-12 px-6 text-sm sm:text-base'}
        ${isOutOfStock
          ? 'bg-gray-300 cursor-not-allowed'
          : 'bg-brand-600 hover:bg-brand-700 active:scale-[0.98] shadow-md shadow-brand-600/20'
        }`}
    >
      {isOutOfStock ? outOfStockLabel : addToCartLabel}
    </button>
  );

  return (
    <div
      className={`${
        isCompact
          ? 'space-y-3'
          : 'rounded-2xl border border-gray-100 bg-gradient-to-b from-slate-50/80 to-white p-4 sm:p-5 shadow-sm space-y-4'
      }`}
    >
      <BundleExplainerCard moq={moq} compact={isCompact} />

      {/* Your order summary */}
      <div
        className={`flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-white px-3 py-2 ${
          isCompact ? '' : 'sm:px-4 sm:py-2.5'
        }`}
      >
        <span className={`text-gray-500 ${isCompact ? 'text-[10px]' : 'text-xs'}`}>Your order</span>
        <div className="text-right">
          <p className={`font-bold text-gray-900 ${isCompact ? 'text-xs' : 'text-sm'}`}>
            {summary.headline}
          </p>
          {summary.detail && (
            <p className={`text-gray-500 ${isCompact ? 'text-[9px]' : 'text-[10px]'}`}>
              {summary.detail}
            </p>
          )}
        </div>
      </div>

      {/* Quick bundle presets */}
      {presets.length > 0 && (
        <div>
          <p
            className={`font-semibold text-gray-500 uppercase tracking-wider mb-2 ${
              isCompact ? 'text-[9px]' : 'text-[11px]'
            }`}
          >
            {moq === 1 ? 'Quick quantities' : 'Choose number of bundles'}
          </p>
          <div
            className={`flex gap-2 overflow-x-auto pb-0.5 -mx-1 px-1 snap-x snap-mandatory scrollbar-hide ${
              isCompact ? '' : 'sm:flex-wrap sm:overflow-visible sm:snap-none'
            }`}
          >
            {presets.map((preset) => {
              const isActive = total === preset.quantity;
              return (
                <button
                  key={preset.quantity}
                  type="button"
                  onClick={() => onChange(preset.quantity)}
                  aria-label={`${preset.primary}, ${preset.secondary}`}
                  className={`snap-start shrink-0 flex flex-col items-center justify-center rounded-xl border transition-all
                    ${isCompact ? 'min-w-[68px] h-[54px] px-2' : 'min-w-[76px] h-[60px] px-2.5 sm:min-w-0 sm:flex-1 sm:max-w-[108px]'}
                    ${
                      isActive
                        ? 'border-brand-500 bg-brand-600 text-white shadow-md ring-2 ring-brand-200/80'
                        : 'border-gray-200 bg-white hover:border-brand-300 hover:shadow-sm'
                    }`}
                >
                  <span
                    className={`font-bold leading-tight text-center ${
                      isCompact ? 'text-xs' : 'text-sm'
                    } ${isActive ? 'text-white' : 'text-gray-900'}`}
                  >
                    {preset.primary}
                  </span>
                  <span
                    className={`mt-0.5 leading-tight text-center ${
                      isCompact ? 'text-[9px]' : 'text-[10px]'
                    } ${isActive ? 'text-brand-100' : 'text-gray-500'}`}
                  >
                    {preset.secondary}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div
        className={`flex gap-2 sm:gap-3 ${
          isCompact ? 'flex-col' : 'flex-col sm:flex-row sm:items-end'
        }`}
      >
        {stepper}
        {addButton}
      </div>

      {(stockHint || availableStock != null) && (
        <p
          className={`flex items-start gap-1.5 text-gray-500 ${
            isCompact ? 'text-[10px] leading-snug' : 'text-xs'
          }`}
        >
          <span
            className={`mt-1 shrink-0 rounded-full ${
              isCompact ? 'w-1 h-1' : 'w-1.5 h-1.5'
            } ${availableStock >= moq ? 'bg-brand-500' : 'bg-red-400'}`}
          />
          <span>{stockHint || `${availableStock} pieces in stock`}</span>
        </p>
      )}
    </div>
  );
}
